import axios from "axios"
import api_url from "../apiPaths"
import { tokenInstance } from "../baseApi"
import { getAuthtoken } from "../../utils/tokens"

/**
 * Staff SSO authentication — `/sso/api/v1/staff/auth`.
 *
 * The flat `/sso/...` routes these used to call are gone; every path below
 * hangs off `api_url.ssoStaff`. Two things changed with them, and both are
 * the server's shape, not a preference:
 *
 *  - requests name the app they are for as `app` ("admin" for Avdhaan),
 *    where they used to send `user_type`. `user_type` still comes *back*
 *    inside `user_detail`, so the header `baseApi` sets is untouched;
 *  - password reset has no separate verify step any more. `password/forgot`
 *    checks the OTP itself, so there is nothing to call between sending the
 *    code and setting the new password.
 *
 * Tokens issued by the old login do not work here. Everyone signs in again
 * once, which is expected and needs no handling.
 */

/**
 * The app these screens sign in to.
 *
 * Staff SSO serves several — `technician` is the other one today — and every
 * auth call names the one it is for. Avdhaan is always `admin`, so it is a
 * constant rather than something a form carries: the login page used to post
 * a hidden `user_type` field, which could only ever be wrong.
 */
export const STAFF_APP = "admin"

// Base axios instance for auth
const authInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"
  }
})

// Add request interceptor to include authorization headers
authInstance.interceptors.request.use(
  (config) => {
    // Add authtoken header if available (for API authorization)
    const authtoken = getAuthtoken()
    if (authtoken) {
      config.headers.authtoken = authtoken
    }
    
    // Some APIs require Authorization header even for login
    // Add empty Authorization header if not present to avoid 401 errors
    if (!config.headers.Authorization && !config.headers.authorization) {
      // You can uncomment below if API requires Authorization header
      // config.headers.Authorization = 'Bearer ' // Empty bearer token
      // Or add a default value if needed
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Login API
 * Endpoint: POST {ssoStaff}/auth/login
 *
 * @param {Object} body - Login credentials
 * @param {string} body.user_name - Username/Email
 * @param {string} body.password - Password
 * @param {string} body.app - App code, "admin" for Avdhaan
 * @returns {Promise} API response
 * 
 * Response structure:
 * {
 *   "response_code": 200,
 *   "data": {
 *     "access_token": "...",
 *     "refresh_token": "...",
 *     "user_detail": {...}
 *   }
 * }
 * 
 * If 2FA is ON, response may include:
 * {
 *   "response_code": 200,
 *   "data": {
 *     "requires_otp": true,
 *     "session_id": "..."
 *   }
 * }
 */
export const loginV2 = async (body) => {
  try {
    const endpoint = `${api_url.ssoStaff}/auth/login`
    
    const response = await authInstance.post(endpoint, body)
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Verify OTP API (for 2FA) — finishes a login, returning the same tokens a
 * password-only login would.
 * Endpoint: POST {ssoStaff}/auth/otp/verify
 *
 * @param {Object} body - OTP verification data
 * @param {string|number} body.otp - OTP code received via email
 * @param {string} body.user_name - Username/Email
 * @param {string} body.app - App code, "admin" for Avdhaan
 * @returns {Promise} API response
 */
export const verifyOtpV2 = async (body) => {
  try {
    const endpoint = `${api_url.ssoStaff}/auth/otp/verify`
    const response = await authInstance.post(endpoint, body)
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Logout API
 * Endpoint: GET {ssoStaff}/auth/logout
 *
 * A GET now, not a POST, and it carries nothing but the token — the
 * signature keeps its argument so the call sites need no edit.
 *
 * @returns {Promise} API response
 */
export const logoutV2 = async () => {
  try {
    const response = await authInstance.get(`${api_url.ssoStaff}/auth/logout`)
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Refresh token API
 * Endpoint: POST {ssoStaff}/auth/refresh
 *
 * @param {Object} body
 * @param {string} body.refresh_token - Refresh token
 * @returns {Promise} API response with new access_token and refresh_token
 */
export const refreshTokenV2 = async (body) => {
  try {
    const response = await authInstance.post(`${api_url.ssoStaff}/auth/refresh`, body)
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Get current user details
 * @returns {Promise} API response with user_detail
 */
export const getCurrentUserV2 = async () => {
  try {
    const { tokenInstanceV2 } = await import("../baseApiV2")
    const response = await tokenInstanceV2.get(
      `${api_url.loginUrl}/auth/user/`
    )
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Send an OTP — for a password reset, or to reissue a login's 2FA code.
 * Endpoint: POST {ssoStaff}/auth/otp/send
 *
 * @param {Object} body
 * @param {string} body.user_name - Username/Email
 * @param {string} body.use_for - "password_reset" or "login"
 * @param {string} body.app - App code, "admin" for Avdhaan
 */
export const forgotPasswordOtpGenerate = async (body) => {
  try {
    const response = await authInstance.post(`${api_url.ssoStaff}/auth/otp/send`, body)
    return response
  } catch (error) {
    return error.response || error
  }
}

/**
 * Reset a forgotten password.
 * Endpoint: POST {ssoStaff}/auth/password/forgot
 *
 * This checks the OTP as well as setting the password — there is no verify
 * call before it any more. Every session of the user ends.
 *
 * @param {Object} body
 * @param {string} body.user_name - Username/Email
 * @param {number} body.otp - The emailed code
 * @param {string} body.new_password
 * @param {string} body.app - App code, "admin" for Avdhaan
 */
export const forgotPasswordResetSso = async (body) => {
  try {
    const response = await authInstance.post(`${api_url.ssoStaff}/auth/password/forgot`, body)
    return response
  } catch (error) {
    return error.response || error
  }
}


/**
 * Change your own password while signed in.
 * Endpoint: POST {ssoStaff}/auth/password/change
 *
 * Every session ends, so the caller sends the user back to login.
 */
export const postUpdatePasswordV2 = async (body) => {
  try {
    const response = await tokenInstance.post(`${api_url.ssoStaff}/auth/password/change`, body)
    return response
  } catch (error) {
    return error.response || error
  }
}


export const getPublicApiKeyV2 = async (config) => {
  try {
    const response = await authInstance.get(`${api_url.base_url}/public-key/`, config)
    return response
  } catch (error) {
    return error.response || error
  }
}

